import React, { useEffect, useRef, useState } from 'react';
import { ActionButton } from "../../components/buttons/ActionButton";
import { CameraIcon, PlayIcon, StopIcon } from "@heroicons/react/20/solid";
import { Loader } from "../../components/loading/Loader";
import { useSocket } from "../../contexts/SocketContext";

export const RecorderStreamPage = () => {
  const { socket, sendEvent } = useSocket();

  const camId = 'external-1';
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [isStarted, setIsStarted] = useState(false);

  const waitIceGatheringComplete = async () => {
    const pc = pcRef.current;
    if (!pc) return;
    if (pc.iceGatheringState === 'complete') return;

    await new Promise((resolve) => {
      const onStateChange = () => {
        const currentPc = pcRef.current;
        if (!currentPc) return;
        if (currentPc.iceGatheringState === 'complete') {
          currentPc.removeEventListener('icegatheringstatechange', onStateChange);
          resolve();
        }
      };

      pc.addEventListener('icegatheringstatechange', onStateChange);
    });
  };

  const stop = () => {
    setIsStreaming(false);

    try {
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

    } finally {
      setIsStarted(false);
    }
  };

  const start = async () => {
    try {
      setIsStarted(true);

      const localStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 10000 },
          height: { ideal: 10000 },
        },
        audio: true
      });

      localStreamRef.current = localStream;

      if (videoRef.current) {
        videoRef.current.srcObject = localStream;
      }

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.onconnectionstatechange = () => {
        const state = pcRef.current?.connectionState || 'unknown';
        console.log(state);
      };

      localStream.getTracks().forEach((track) => {
        pc.addTrack(track, localStream);
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      await waitIceGatheringComplete();

      const response = await fetch(`${process.env.REACT_APP_ADDRESS}/streaming/offer/${encodeURIComponent(camId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sdp: pc.localDescription.sdp,
          type: pc.localDescription.type
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const answer = await response.json();
      await pc.setRemoteDescription(answer);

      setIsStreaming(true);

      if (!socket) return;
      sendEvent("current_data", { start_stream: true });
    } catch (error) {
      console.error(error);
      setIsStarted(false);
    }
  };

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  return (
    <div className="h-full flex flex-col justify-center items-center gap-8">
      <div className={`absolute top-0 left-0 w-full h-full
        ${(!isStreaming && !isStarted) && 'opacity-0'}
        ${!isStreaming && 'pointer-events-none'}
      `}>
        <video
          ref={videoRef}
          autoPlay
          // controls
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />

        {
          isStreaming &&
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <ActionButton text="Arrêter" Icon={StopIcon} onClick={stop}/>
            </div>
        }
      </div>
      {
        !isStreaming && isStarted ?
          <Loader/>
        : !isStarted ?
          <div className="flex flex-col justify-center items-center gap-8 z-50">
            <CameraIcon className="text-perception-gray-700 size-32"/>
            <ActionButton text="Démarrer la captation" Icon={PlayIcon} size="lg" onClick={start} disabled={isStarted}/>
          </div>
        : null
      }
    </div>
  );
};